import { getStoredToken, type PortalKind } from '@/lib/auth/token-storage'

export const adminPasswordResetRequiredKey = 'admin_password_reset_required'
export const userPasswordResetRequiredKey = 'user_password_reset_required'

const portalPasswordResetRequiredKey: Record<PortalKind, string> = {
  admin: adminPasswordResetRequiredKey,
  user: userPasswordResetRequiredKey,
}

export const portalDefaultPath: Record<PortalKind, '/admin/overview' | '/chat'> = {
  admin: '/admin/overview',
  user: '/chat',
}

export const portalLoginRoutePath: Record<PortalKind, '/admin/login' | '/login'> = {
  admin: '/admin/login',
  user: '/login',
}

export const portalPasswordResetRoutePath: Record<
  PortalKind,
  '/admin/reset-password' | '/reset-password'
> = {
  admin: '/admin/reset-password',
  user: '/reset-password',
}

/**
 * 根据路由前缀推断当前门户。
 * 未命中 `/admin` 的路径都按用户端处理，保证 unknown route 能回到正确入口。
 */
export function resolvePortalFromPath(pathname: string): PortalKind {
  return pathname === '/admin' || pathname.startsWith('/admin/') ? 'admin' : 'user'
}

export function isAdminPasswordResetRequired() {
  return isPortalPasswordResetRequired('admin')
}

export function isPortalPasswordResetRequired(portal: PortalKind) {
  if (typeof window === 'undefined') {
    return false
  }

  return window.localStorage.getItem(portalPasswordResetRequiredKey[portal]) === 'true'
}

/**
 * 判断门户是否拥有可进入受保护页面的本地 session。
 * 强制改密标记会让 token 暂时失效于路由 guard，避免用户绕过改密页面。
 */
export function hasPortalSession(portal: PortalKind) {
  if (!getStoredToken(portal)) {
    return false
  }

  if (isPortalPasswordResetRequired(portal)) {
    return false
  }

  return true
}

/**
 * 解析门户根入口应该跳转到登录、强制改密还是默认首页。
 */
export function resolvePortalEntryPath(portal: PortalKind) {
  if (!getStoredToken(portal)) {
    return portalLoginRoutePath[portal]
  }

  if (isPortalPasswordResetRequired(portal)) {
    return portalPasswordResetRoutePath[portal]
  }

  return portalDefaultPath[portal]
}

export function resolveUnknownRouteEntryPath(pathname: string) {
  return resolvePortalEntryPath(resolvePortalFromPath(pathname))
}

/**
 * 受保护路由的 UX guard。
 * 这里只处理本地跳转体验，真实鉴权和授权仍由后端 API 决定。
 */
export function resolveProtectedRouteRedirectPath(portal: PortalKind) {
  if (!getStoredToken(portal)) {
    return portalLoginRoutePath[portal]
  }

  if (isPortalPasswordResetRequired(portal)) {
    return portalPasswordResetRoutePath[portal]
  }

  return undefined
}
